/**
 * Root app component for Meridian mobile.
 *
 * Loads contacts from Apple via the Tauri contacts plugin on iOS,
 * falls back to an empty list on desktop/web. Manages tab navigation,
 * contact selection, search, and bidirectional sync with Apple.
 *
 * The sync model: local state is the source of truth for rendering.
 * Apple contacts flow in via useContacts, merge into local state
 * preserving Suron-only fields, and edits to Apple-owned fields
 * flow back out through a pendingSyncRef that batches writes until
 * React state has settled.
 */
import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import { ChevronRight } from "lucide-react";
import {
  NavBar,
  type Tab,
  type DetailTab,
  type NavMode,
  type IdentityState,
} from "@/layout/NavBar";
import { ContactList } from "@/people/ContactList";
import { ContactDetail } from "@/people/ContactDetail";
import { Conversation } from "@/conversation/Conversation";
import { Me } from "@/me/Me";
import { Onboarding } from "@/onboarding/Onboarding";
import { loadProfile } from "@/onboarding/profileStorage";
import type { UserProfile } from "@/onboarding/types";
import { ErrorBoundary } from "@/ui/ErrorBoundary";
import { DevGate } from "@/developer/DevGate";
import { type Contact } from "@/lib/data";
import { useContacts } from "@/hooks/useContacts";
import {
  setDevRecordScreenOpen,
  useDevRecordScreen,
} from "@/hooks/useDevRecordScreen";
import { listAllSignals } from "@/hooks/useSessionProcessor";
import type { ContactSignal } from "@/types/voice";
import {
  searchSignalsByTopic,
  formatMatchReason,
  type MatchedSignal,
} from "@/utils/topicSearch/topicSearch";

/**
 * Fields owned by Apple Contacts. When any of these change in Meridian,
 * the update is written back to Apple via the contacts plugin.
 * Suron-only fields (status, move, path, tags, etc.) are excluded
 * because they only exist in Meridian's local/cloud storage.
 */
const APPLE_SYNCED_FIELDS = new Set([
  "firstName", "lastName", "middleName", "namePrefix", "nameSuffix",
  "nickname", "birthday", "phones", "emails", "addresses", "socials",
  "jobs", "notes", "relations", "dates", "photo",
]);

export function App() {
  const [profile, setProfile] = useState<UserProfile | null>(() => loadProfile());

  if (!profile) {
    return (
      <>
        <Onboarding onComplete={setProfile} />
        <DevGate />
      </>
    );
  }

  return (
    <>
      <EnrolledApp profile={profile} />
      <DevGate />
      <DevRecordScreen />
    </>
  );
}

/**
 * Full-screen dev-only wrapper for the legacy Conversation scaffold.
 * Opened from Voice Diagnostics → Data → "Open record controls".
 * Kept separate from the Me tab so the scaffold's dev-ness is
 * unambiguous and the Me tab is free to evolve.
 */
function DevRecordScreen() {
  const open = useDevRecordScreen();
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[9700] flex flex-col bg-background text-foreground">
      <header
        className="flex items-center justify-between border-b border-yellow-400/30 px-4"
        style={{
          paddingTop: "calc(env(safe-area-inset-top, 0px) + 12px)",
          paddingBottom: "12px",
        }}
      >
        <div className="flex flex-col">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-yellow-400/80">
            Developer mode
          </p>
          <h2 className="text-[18px] font-semibold">Record controls</h2>
        </div>
        <button
          type="button"
          onClick={() => setDevRecordScreenOpen(false)}
          aria-label="Close record controls"
          className="rounded-full border border-[rgba(255,255,255,0.1)] px-3 py-1.5 text-[13px] text-white/70 active:scale-95"
        >
          Done
        </button>
      </header>
      <div className="flex-1 overflow-y-auto">
        <Conversation />
      </div>
    </div>
  );
}

function EnrolledApp({ profile }: { profile: UserProfile }) {
  const { contacts: appleContacts, loading, permissionStatus, updateAppleContact } = useContacts();
  const [tab, setTab] = useState<Tab>("people");
  const [detailTab, setDetailTab] = useState<DetailTab>("overview");
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [contacts, setContacts] = useState<Contact[]>([]);
  // Phase B.5 — signals are fetched lazily the first time search opens
  // and then cached for the session. Small payload (few hundred rows
  // max) so no invalidation strategy needed beyond "reload when app
  // restarts"; fresh signals land via listAllSignals when it matters.
  const [allSignals, setAllSignals] = useState<ContactSignal[] | null>(null);
  useEffect(() => {
    if (!searchOpen || allSignals !== null) return;
    let cancelled = false;
    listAllSignals()
      .then((rows) => {
        if (!cancelled) setAllSignals(rows);
      })
      .catch(() => {
        if (!cancelled) setAllSignals([]);
      });
    return () => {
      cancelled = true;
    };
  }, [searchOpen, allSignals]);

  // Merge incoming Apple contacts into local state. Preserves Suron-only
  // fields (status, move, tags, etc.) while taking Apple fields fresh.
  const prevAppleRef = useRef(appleContacts);
  useEffect(() => {
    if (appleContacts === prevAppleRef.current) return;
    prevAppleRef.current = appleContacts;

    setContacts((prev) => {
      if (prev.length === 0) return appleContacts;
      const existing = new Map(prev.map((c) => [c.appleContactId ?? c.id, c]));
      return appleContacts.map((ac) => {
        const local = existing.get(ac.id);
        if (!local) return ac;
        // Preserve Suron-only fields from local state, take Apple fields from fresh data
        return {
          ...ac,
          status: local.status,
          move: local.move,
          path: local.path,
          tags: local.tags,
          favorite: local.favorite,
          claimed: local.claimed,
          claimedAt: local.claimedAt,
          connectionStatus: local.connectionStatus,
          lastInteraction: local.lastInteraction,
          interactionCount: local.interactionCount,
          enrichment: local.enrichment,
          lastViewedAt: local.lastViewedAt,
          viewCount: local.viewCount,
        };
      });
    });
  }, [appleContacts]);

  // Name/phone/job matches carry no match reason (the name itself is
  // the reason). Topic matches carry the specific matched signal so
  // the UI can show "Interested in climbing" on the result row.
  interface SearchHit {
    contact: Contact;
    matchedSignal: MatchedSignal | null;
  }

  const searchResults = useMemo((): SearchHit[] => {
    if (!searchQuery.trim()) return [];
    const q = searchQuery.toLowerCase();

    // 1. Conventional contact-field matches (name, phone, job).
    const conventional: SearchHit[] = contacts
      .filter(
        (c) =>
          c.firstName.toLowerCase().includes(q) ||
          c.lastName.toLowerCase().includes(q) ||
          `${c.firstName} ${c.lastName}`.toLowerCase().includes(q) ||
          c.phones.some((p) => p.number.includes(q)) ||
          c.jobs.some(
            (j) =>
              j.title.toLowerCase().includes(q) ||
              j.company.toLowerCase().includes(q)
          )
      )
      .map((c) => ({ contact: c, matchedSignal: null }));

    // 2. Phase B.5 — topic matches via the signal graph. Skip any
    // contactId that's already in the conventional results so a
    // single person never appears twice; if they matched by name,
    // their name is the clearer reason to show.
    const alreadyMatched = new Set(
      conventional.map((h) => h.contact.appleContactId ?? h.contact.id)
    );
    const byApple = new Map(
      contacts
        .filter((c) => c.appleContactId)
        .map((c) => [c.appleContactId!, c])
    );
    const topicHits: SearchHit[] = [];
    if (allSignals && allSignals.length > 0) {
      const matches = searchSignalsByTopic(allSignals, searchQuery);
      for (const m of matches) {
        if (alreadyMatched.has(m.contactId)) continue;
        const contact = byApple.get(m.contactId);
        if (!contact) continue;
        topicHits.push({
          contact,
          matchedSignal: m.matchedSignals[0] ?? null,
        });
      }
    }

    return [...conventional, ...topicHits].slice(0, 30);
  }, [searchQuery, contacts, allSignals]);

  const handleSelectContact = (contact: Contact) => {
    setDetailTab("overview");
    setSelectedContact(contact);
    handleUpdateContact(contact.id, {
      lastViewedAt: new Date().toISOString(),
      viewCount: (contact.viewCount ?? 0) + 1,
    });
  };

  const handleBack = () => setSelectedContact(null);

  /**
   * Pending sync ref holds the most recent Apple-field update until
   * React state settles. The effect below picks it up and writes it
   * back to Apple. Using a ref (not state) avoids extra renders and
   * prevents the setState-inside-setState pattern that crashes React 19.
   */
  const pendingSyncRef = useRef<{ id: string; updates: Partial<Contact> } | null>(null);

  /**
   * Update a contact in local state. If the update includes any Apple-owned
   * field, queue it for sync via pendingSyncRef. The actual write to Apple
   * happens in the effect below, after state has settled.
   */
  const handleUpdateContact = useCallback(
    (contactId: string, updates: Partial<Contact>) => {
      const ts = new Date().toISOString();

      setContacts((prev) =>
        prev.map((c) =>
          c.id === contactId ? { ...c, ...updates, updatedAt: ts } : c
        )
      );
      setSelectedContact((prev) =>
        prev && prev.id === contactId
          ? { ...prev, ...updates, updatedAt: ts }
          : prev
      );

      const hasAppleField = Object.keys(updates).some((k) =>
        APPLE_SYNCED_FIELDS.has(k)
      );
      if (hasAppleField) {
        pendingSyncRef.current = { id: contactId, updates };
      }
    },
    []
  );

  // Flush pending Apple sync after contacts state has settled.
  // Reads the full contact from state so the write-back has all fields.
  useEffect(() => {
    const pending = pendingSyncRef.current;
    if (!pending) return;
    pendingSyncRef.current = null;

    const contact = contacts.find((c) => c.id === pending.id);
    if (contact?.appleContactLinked) {
      updateAppleContact(contact);
    }
  }, [contacts, updateAppleContact]);

  const isDetail = selectedContact !== null;
  const navMode: NavMode = isDetail ? "detail" : searchOpen ? "search" : "tabs";

  const identityState: IdentityState = selectedContact
    ? selectedContact.claimed && selectedContact.connectionStatus === "connected"
      ? "verified"
      : selectedContact.claimed && selectedContact.connectionStatus === "pending"
        ? "pending"
        : "local"
    : null;

  const effectiveDetailTab = identityState === "pending" ? "overview" : detailTab;

  if (loading) {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center text-foreground">
        <div className="text-center">
          <div className="mx-auto h-8 w-8 animate-spin rounded-full border-2 border-[rgba(255,255,255,0.1)] border-t-[rgba(255,255,255,0.4)]" />
          <p className="mt-4 text-[14px] text-[rgba(255,255,255,0.4)]">Loading contacts...</p>
        </div>
      </div>
    );
  }

  if (permissionStatus === "denied" || permissionStatus === "restricted") {
    return (
      <div className="bg-background flex min-h-screen items-center justify-center px-8 text-foreground">
        <div className="text-center">
          <p className="text-[18px] font-medium text-white">Contacts Access Required</p>
          <p className="mt-2 text-[14px] leading-relaxed text-[rgba(255,255,255,0.4)]">
            Meridian needs access to your contacts to show your people.
            Open Settings to grant access.
          </p>
          <button
            onClick={() => {
              // On iOS, this opens the app's settings page
              window.open("app-settings:", "_self");
            }}
            className="mt-6 rounded-full bg-white/10 px-6 py-2.5 text-[14px] font-medium text-white active:scale-95"
          >
            Open Settings
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background min-h-screen text-foreground">

        {/* Search results */}
        {!isDetail && searchOpen && (
          <div className="flex flex-col pt-[calc(env(safe-area-inset-top,0px)+16px)]" style={{ paddingBottom: 120 }}>
            <div className="px-5">
              {searchQuery.length > 0 ? (
                searchResults.length > 0 ? (
                  searchResults.map((hit, i) => {
                    const contact = hit.contact;
                    const currentJob = contact.jobs.find((j) => j.current);
                    const secondary = hit.matchedSignal
                      ? formatMatchReason(hit.matchedSignal)
                      : currentJob
                        ? `${currentJob.title}${currentJob.company ? ` · ${currentJob.company}` : ""}`
                        : null;
                    const secondaryColor = hit.matchedSignal
                      ? "text-[rgba(120,180,255,0.7)]"
                      : "text-[rgba(255,255,255,0.4)]";
                    return (
                      <button
                        key={`${contact.id}-${hit.matchedSignal?.value ?? ""}`}
                        onClick={() => {
                          setSearchOpen(false);
                          setSearchQuery("");
                          handleSelectContact(contact);
                        }}
                        className="flex w-full items-center gap-3 py-3 text-left transition-all active:opacity-70"
                      >
                        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[rgba(255,255,255,0.06)]">
                          <span className="text-[14px] text-[rgba(255,255,255,0.5)]">
                            {contact.firstName[0]}
                            {contact.lastName[0]}
                          </span>
                        </div>
                        <div
                          className={`flex min-w-0 flex-1 items-center gap-2 ${i < searchResults.length - 1 ? "border-b border-[rgba(255,255,255,0.06)] pb-3" : ""}`}
                        >
                          <div className="min-w-0 flex-1">
                            <p className="text-[16px] text-white">
                              {contact.firstName} {contact.lastName}
                            </p>
                            {secondary && (
                              <p className={`truncate text-[13px] ${secondaryColor}`}>
                                {secondary}
                              </p>
                            )}
                          </div>
                          <ChevronRight size={16} className="shrink-0 text-[rgba(255,255,255,0.15)]" />
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <p className="py-16 text-center text-[14px] text-[rgba(255,255,255,0.3)]">
                    No results for &ldquo;{searchQuery}&rdquo;
                  </p>
                )
              ) : (
                <div className="py-16 text-center">
                  <p className="text-[15px] text-[rgba(255,255,255,0.4)]">Search your contacts</p>
                  <p className="mt-1 text-[13px] text-[rgba(255,255,255,0.2)]">By name, job, or topic</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab content */}
        {!isDetail && !searchOpen && tab === "me" && (
          <Me profile={profile} contacts={contacts} />
        )}

        {!isDetail && !searchOpen && tab === "people" && (
          <ContactList
            contacts={contacts}
            onSelectContact={handleSelectContact}
            onUpdateContact={handleUpdateContact}
          />
        )}

        {!isDetail && !searchOpen && tab === "circles" && (
          <div className="px-5 pb-28 pt-[calc(env(safe-area-inset-top,0px)+16px)]">
            <h1 className="text-foreground text-[34px] font-bold tracking-tight">Circles</h1>
            <p className="text-muted-foreground mt-1 text-[14px]">Your groups and communities.</p>
          </div>
        )}

        {!isDetail && !searchOpen && tab === "paths" && (
          <div className="px-5 pb-28 pt-[calc(env(safe-area-inset-top,0px)+16px)]">
            <h1 className="text-foreground text-[34px] font-bold tracking-tight">Paths</h1>
            <p className="text-muted-foreground mt-1 text-[14px]">Who do you know who knows...</p>
          </div>
        )}

        {/* Detail */}
        {isDetail && (
          <ErrorBoundary>
            <ContactDetail
              contact={selectedContact}
              activeTab={effectiveDetailTab}
              onTabChange={setDetailTab}
              onContactChange={handleUpdateContact}
            />
          </ErrorBoundary>
        )}

        {/* end tab content — fall through to NavBar */}

        <NavBar
          mode={navMode}
          activeTab={tab}
          activeDetailTab={effectiveDetailTab}
          onTabChange={setTab}
          onDetailTabChange={setDetailTab}
          onSearchToggle={() => {
            if (searchOpen) {
              setSearchOpen(false);
              setSearchQuery("");
            } else {
              setSearchOpen(true);
            }
          }}
          onBack={handleBack}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          identityState={identityState}
          isSearchMode={searchOpen}
        />
    </div>
  );
}

